"use client";

import { useEffect, useRef, useState, type FormEvent, type MouseEvent, type ReactNode } from "react";
import { useSearchParams } from "next/navigation";
import type { AiToolId } from "@cw/forms/ai-tools";
import { Container } from "@cw/ui";
import type { BundlesData } from "@/lib/cms";
import { submitPhaseOne } from "@/get-started-actions";
import { BundleComparisonDialog } from "./BundleComparisonDialog";
import { AiToolTiles } from "./get-started/AiToolTiles";
import { BundleTierCards } from "./get-started/BundleTierCards";
import { ConfigurationSummary } from "./get-started/ConfigurationSummary";
import { GetStartedReceipt } from "./get-started/GetStartedReceipt";
import { IntakeProgress, type ProgressStep } from "./get-started/IntakeProgress";
import { SectionPanel, SelectField, TextField } from "./get-started/fields";
import {
  CRM_SUGGESTIONS, EMPTY_ENVIRONMENT, ERP_SUGGESTIONS, MIGRATION_OPTIONS, PLATFORM_SUGGESTIONS, STEPS,
  completedSteps, type Environment, type StepId,
} from "./get-started/intake-options";

const PRIMARY_BUTTON = "inline-flex min-h-13 w-full items-center justify-center gap-3 rounded-lg border border-cta bg-cta px-6 py-3 text-sm font-semibold text-cta-ink transition-colors hover:bg-cta-hover focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ink disabled:cursor-wait disabled:opacity-60";

const scrollToAnchor = (anchor: string, focus = true) => {
  const target = document.getElementById(anchor);
  if (!target) return;
  target.scrollIntoView({ block: "start" });
  if (focus) target.querySelector<HTMLElement>("h2, h3")?.focus?.({ preventScroll: true });
};

/** Scroll-spy over the stage panels so the progress indicator follows the reader. */
function useActiveStep(enabled: boolean): StepId {
  const [active, setActive] = useState<StepId>("bundle");
  useEffect(() => {
    if (!enabled || !("IntersectionObserver" in window)) return;
    const visible = new Map<StepId, number>();
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const step = STEPS.find((candidate) => candidate.anchor === entry.target.id);
        if (!step) continue;
        if (entry.isIntersecting) visible.set(step.id, entry.boundingClientRect.top);
        else visible.delete(step.id);
      }
      const nearest = [...visible.entries()].sort((a, b) => a[1] - b[1])[0];
      if (nearest) setActive(nearest[0]);
    }, { rootMargin: "-25% 0px -55% 0px", threshold: [0, 0.25, 0.5, 1] });
    for (const step of STEPS) {
      const element = document.getElementById(step.anchor);
      if (element) observer.observe(element);
    }
    return () => observer.disconnect();
  }, [enabled]);
  return active;
}

export function GetStartedForm({ data, comparison }: { data: BundlesData; comparison?: ReactNode }) {
  const params = useSearchParams();
  const initialBundle = data.bundles.find((bundle) => bundle.id === params.get("bundle"))?.id ?? data.bundles[0]?.id ?? "";
  const [bundleId, setBundleId] = useState(initialBundle);
  const [environment, setEnvironment] = useState<Environment>(EMPTY_ENVIRONMENT);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [pending, setPending] = useState(false);
  const [receipt, setReceipt] = useState<{ id: string; confirmationSent: boolean } | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const inFlight = useRef(false);
  const submissionKey = useRef("");
  const selected = data.bundles.find((bundle) => bundle.id === bundleId);
  const activeStep = useActiveStep(!receipt);

  useEffect(() => {
    const id = params.get("bundle");
    if (!inFlight.current && !receipt && id && data.bundles.some((bundle) => bundle.id === id)) {
      setBundleId(id);
      submissionKey.current = "";
    }
  }, [params, data.bundles, receipt]);

  function update<K extends keyof Environment>(key: K, value: Environment[K]) {
    setEnvironment((current) => ({ ...current, [key]: value }));
    setErrors((current) => {
      const next = { ...current };
      for (const errorKey of Object.keys(next)) if (errorKey === key || errorKey.startsWith(`${key}.`)) delete next[errorKey];
      return next;
    });
    submissionKey.current = "";
  }

  function toggleAiTool(id: AiToolId) {
    update("aiTools", environment.aiTools.includes(id) ? environment.aiTools.filter((tool) => tool !== id) : [...environment.aiTools, id]);
  }

  function chooseBundle(id: string) {
    setBundleId(id);
    setErrors((current) => { const next = { ...current }; delete next.bundle; return next; });
    submissionKey.current = "";
  }

  function focusFirstError(fieldErrors: Record<string, string>) {
    const firstKey = Object.keys(fieldErrors).find((key) => key !== "_form" && key !== "submissionKey");
    const anchor = STEP_FOR_FIELD[firstKey?.split(".")[0] ?? ""] ?? "edge-step-bundle";
    requestAnimationFrame(() => {
      scrollToAnchor(anchor, false);
      document.getElementById(`edge-${firstKey}`)?.focus({ preventScroll: true });
    });
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (inFlight.current || receipt || !selected) return;

    const website = String(new FormData(event.currentTarget).get("website") ?? "");
    if (!submissionKey.current) submissionKey.current = crypto.randomUUID();
    inFlight.current = true;
    setPending(true);
    setErrors({});
    try {
      const result = await submitPhaseOne({ ...environment, bundle: bundleId, submissionKey: submissionKey.current, website });
      if (result.ok) {
        setReceipt({ id: result.id, confirmationSent: result.confirmationSent });
        requestAnimationFrame(() => heading.current?.focus());
      } else {
        setErrors(result.errors);
        const hasFieldError = Object.keys(result.errors).some((key) => key !== "_form" && key !== "submissionKey");
        if (hasFieldError) focusFirstError(result.errors);
      }
    } catch {
      setErrors({ _form: "We couldn’t confirm your request. Please try again, or email edge@pscx.ai." });
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  }

  const done = completedSteps(environment, Boolean(selected));
  const steps: ProgressStep[] = STEPS.map((step) => ({
    ...step,
    status: step.id === activeStep ? "current" : done.has(step.id) ? "complete" : "upcoming",
  }));

  function selectStep(step: ProgressStep, event: MouseEvent<HTMLAnchorElement>) {
    event.preventDefault();
    scrollToAnchor(step.anchor);
  }

  if (receipt) {
    return <GetStartedReceipt receipt={receipt} environment={environment} bundleName={selected?.name ?? "Selected bundle"} headingRef={heading} />;
  }

  const aiToolsError = errors.aiTools ?? Object.entries(errors).find(([key]) => key.startsWith("aiTools."))?.[1];

  return (
    <section id="get-started-form" className="scroll-mt-24 bg-paper pb-20 pt-10 sm:pt-12">
      <Container>
        <header className="max-w-3xl">
          <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.2em] text-signal-ink"><span aria-hidden="true" className="h-0.5 w-10 rounded-pill bg-signal" />Get Started</p>
          <h1 ref={heading} tabIndex={-1} className="mt-4 font-display text-3xl font-semibold tracking-tight text-ink outline-none sm:text-4xl">Configure Your Edge</h1>
          <p className="mt-3 text-base leading-7 text-slate">We use these details to recommend the right operating model and migration approach for your organization.</p>
        </header>

        <div className="mt-8">
          <IntakeProgress steps={steps} onSelect={selectStep} />
        </div>

        <div className="mt-6 grid items-start gap-6 lg:grid-cols-[minmax(0,7fr)_minmax(0,3fr)] lg:gap-8">
          <form id="edge-intake" onSubmit={handleSubmit} aria-busy={pending} className="min-w-0 space-y-6">
            <fieldset disabled={pending} className="min-w-0 space-y-6">
              <SectionPanel id="edge-step-bundle" step={1} title="Select your Edge" description="Choose the operating tier that matches how much control and automation you want." actions={comparison ? <BundleComparisonDialog>{comparison}</BundleComparisonDialog> : undefined}>
                <BundleTierCards data={data} selectedId={bundleId} onSelect={chooseBundle} error={errors.bundle} />
              </SectionPanel>

              <SectionPanel id="edge-step-organization" step={2} title="Organization profile" description="Size the engagement. Whole numbers are fine; estimates are expected.">
                <div className="grid gap-5 sm:grid-cols-2">
                  <TextField id="edge-totalUsers" name="totalUsers" label="Total users" required type="number" inputMode="numeric" max={1_000_000} placeholder="e.g. 850" hint="Licensed users across all communications platforms." value={environment.totalUsers} onChange={(value) => update("totalUsers", value)} error={errors.totalUsers} />
                  <TextField id="edge-totalSites" name="totalSites" label="Total sites" required type="number" inputMode="numeric" max={100_000} placeholder="e.g. 12" hint="Offices, branches, or locations with their own numbering or devices." value={environment.totalSites} onChange={(value) => update("totalSites", value)} error={errors.totalSites} />
                </div>
              </SectionPanel>

              <SectionPanel id="edge-step-technology" step={3} title="Technology environment" description="What Edge will connect to. Everything here is optional and helps us plan integrations.">
                <div className="grid gap-5 sm:grid-cols-2">
                  <TextField id="edge-currentPlatform" name="currentPlatform" label="Current UC/CX platform" maxLength={120} placeholder="Start typing to search" hint="Choose a suggestion or enter your own." suggestions={PLATFORM_SUGGESTIONS} value={environment.currentPlatform} onChange={(value) => update("currentPlatform", value)} error={errors.currentPlatform} />
                  <SelectField id="edge-planningToMigrate" name="planningToMigrate" label="Planning to migrate?" options={MIGRATION_OPTIONS} hint="Your timeframe shapes how we scope the included migration." value={environment.planningToMigrate} onChange={(value) => update("planningToMigrate", value)} error={errors.planningToMigrate} />
                  <TextField id="edge-crm" name="crm" label="CRM" maxLength={120} placeholder="e.g. Salesforce" suggestions={CRM_SUGGESTIONS} value={environment.crm} onChange={(value) => update("crm", value)} error={errors.crm} />
                  <TextField id="edge-erp" name="erp" label="ERP" maxLength={120} placeholder="e.g. Oracle NetSuite" suggestions={ERP_SUGGESTIONS} value={environment.erp} onChange={(value) => update("erp", value)} error={errors.erp} />
                </div>
                <div className="mt-7 border-t border-hairline pt-6">
                  <AiToolTiles value={environment.aiTools} onToggle={toggleAiTool} error={aiToolsError} />
                </div>
              </SectionPanel>

              <SectionPanel id="edge-step-contact" step={4} title="Contact details" description="Who the Edge specialist should reach with the recommendation.">
                <div className="grid gap-5 sm:grid-cols-2">
                  <TextField id="edge-company" name="company" label="Company" required maxLength={160} autoComplete="organization" placeholder="Organization name" value={environment.company} onChange={(value) => update("company", value)} error={errors.company} />
                  <TextField id="edge-name" name="name" label="Your name" required maxLength={120} autoComplete="name" placeholder="First and last name" value={environment.name} onChange={(value) => update("name", value)} error={errors.name} />
                  <TextField id="edge-email" name="email" label="Work email" required type="email" inputMode="email" maxLength={200} autoComplete="email" placeholder="you@company.com" hint="We send a copy of your configuration here." value={environment.email} onChange={(value) => update("email", value)} error={errors.email} />
                  <TextField id="edge-phone" name="phone" label="Phone" type="tel" inputMode="tel" maxLength={40} autoComplete="tel" placeholder="Optional" value={environment.phone} onChange={(value) => update("phone", value)} error={errors.phone} />
                </div>
                <p className="mt-5 text-xs leading-5 text-slate"><span aria-hidden="true" className="text-signal-ink">*</span> Required.</p>
              </SectionPanel>

            </fieldset>

            <div className="absolute -left-[10000px]" aria-hidden="true"><label htmlFor="edge-website">Website</label><input id="edge-website" name="website" tabIndex={-1} autoComplete="off" /></div>

            <div className="rounded-[10px] border border-hairline bg-paper-raised p-6 sm:p-8">
              {errors._form ? <p role="alert" className="mb-5 rounded-lg border border-signal-ink/30 bg-signal-soft px-4 py-3 text-sm font-medium text-signal-ink">{errors._form}</p> : null}
              {errors.submissionKey ? <p role="alert" className="mb-5 rounded-lg border border-signal-ink/30 bg-signal-soft px-4 py-3 text-sm font-medium text-signal-ink">{errors.submissionKey}</p> : null}
              <div>
                <button type="submit" disabled={pending || !selected} className={PRIMARY_BUTTON}>
                  {pending ? "Submitting…" : "Submit Your Configuration"}
                  {!pending ? <span aria-hidden="true">→</span> : null}
                </button>
              </div>
              <p className="mt-4 text-center text-xs leading-5 text-slate">No commitment required. An Edge specialist will review your configuration before anything is finalized.</p>
            </div>
          </form>

          <ConfigurationSummary data={data} selected={selected} environment={environment} />
        </div>
      </Container>
    </section>
  );
}

const STEP_FOR_FIELD: Record<string, string> = {
  bundle: "edge-step-bundle",
  totalUsers: "edge-step-organization",
  totalSites: "edge-step-organization",
  currentPlatform: "edge-step-technology",
  planningToMigrate: "edge-step-technology",
  crm: "edge-step-technology",
  erp: "edge-step-technology",
  aiTools: "edge-step-technology",
  company: "edge-step-contact",
  name: "edge-step-contact",
  email: "edge-step-contact",
  phone: "edge-step-contact",
};
